import type { NavigationMenuItem } from '@nuxt/ui'
import { splitByCase, titleCase } from 'scule'
import { Permissions } from '@package/jwt/nuxt'
import {
  debugPolicyStudioRoutes,
  reviewPolicyStudioRoutes,
} from '~/utils/policy-studio/routes'
import { useApiConfig } from '~/queries/config'

export const AUTH0_ROLE_NAMES = {
  allClientsViewer: {
    id: 'rialtic::app_rbac-All-Clients-Viewer',
    label: 'All Clients Viewer',
  },

  // Client team roles
  clientAnalytics: {
    id: 'rialtic::app_rbac-Client-Analytics',
    label: 'Client Analytics',
  },
  clientExperience: {
    id: 'rialtic::app_rbac-Client-Experience',
    label: 'Client Experience',
  },
  clientSuccess: {
    id: 'rialtic::app_rbac-Client-Success',
    label: 'Client Success',
  },

  // Content team roles
  content: {
    id: 'rialtic::app_rbac-Content',
    label: 'Content',
  },
  contentContractor: {
    id: 'rialtic::app_rbac-Content-Contractor',
    label: 'Content Contractor',
  },
  contentManager: {
    id: 'rialtic::app_rbac-Content-Manager',
    label: 'Content Manager',
  },

  design: {
    id: 'rialtic::app_rbac-Design',
    label: 'Design',
  },

  // Engineering team roles
  engineeringCloud: {
    id: 'rialtic::app_rbac-Engineering-Cloud',
    label: 'Engineering Cloud',
  },
  engineeringCoreClaims: {
    id: 'rialtic::app_rbac-Engineering-Core-Claims',
    label: 'Engineering Core Claims',
  },
  engineeringEngines: {
    id: 'rialtic::app_rbac-Engineering-Engines',
    label: 'Engineering Engines',
  },
  engineeringWorkflows: {
    id: 'rialtic::app_rbac-Engineering-Workflows',
    label: 'Engineering Workflows',
  },
  gtm: { id: 'rialtic::app_rbac-GTM', label: 'GTM' },

  it: { id: 'rialtic::app_rbac-IT', label: 'IT' },

  phi: { id: 'rialtic::app_rbac-PHI', label: 'PHI' },

  product: {
    id: 'rialtic::app_rbac-Product',
    label: 'Product',
  },

  sales: {
    id: 'rialtic::app_rbac-Sales',
    label: 'Sales',
  },
} as const

export const useTeamNav = () => {
  const { session, user } = useUserSession()

  const clientSuccessItems = computed(() =>
    session.value?.permissions?.includes(Permissions.readUsersAll)
      ? [
          {
            label: 'Users',
            icon: 'i-ph-user-list',
            to: '/users',
          },
          {
            label: 'Organizations',
            icon: 'i-ph-building-office',
            to: '/organizations',
          },
        ]
      : [],
  )
  const workspaceItems = computed(() =>
    session.value?.permissions?.includes(Permissions.readWorkspacesAll)
      ? [
          {
            label: 'Workspaces',
            icon: 'i-ph-squares-four',
            to: '/workspaces',
          },
        ]
      : [],
  )

  const { data: config } = useApiConfig()

  const defaultItems = computed(() => [
    ...clientSuccessItems.value,
    ...workspaceItems.value,
    ...(config.value?.analyticsEngineAvailable
      ? [
          {
            label: 'Analytics',
            icon: 'i-ph-chart-bar',
            to: '/analytics',
          },
        ]
      : []),
  ])

  const teamNavItems = computed<Record<string, NavigationMenuItem[]>>(() => ({
    [AUTH0_ROLE_NAMES.clientExperience.id]: [
      {
        label: 'Client Experience',
        icon: 'i-ph-users-three',
        defaultOpen: true,
        children: [
          {
            label: 'Import client review status',
            to: '/imports/client-review-status',
          },
          ...(session.value?.permissions?.includes(
            Permissions.readWorkspacesAll,
          )
            ? [
                {
                  label: 'Insight Mode Templates',
                  icon: 'i-ph-copy',
                  to: '/insight-mode-templates',
                },
              ]
            : []),
        ],
      },
    ],
    [AUTH0_ROLE_NAMES.engineeringWorkflows.id]: [
      {
        label: 'Workflows',
        icon: 'i-ph-rocket',
        defaultOpen: true,
        children: [
          {
            label: 'Monitors Control Panel',
            icon: 'i-ph-security-camera',
            to: '/monitors-control-panel',
          },
          {
            label: 'System',
            icon: 'i-ph-faders-horizontal',
            to: '/system',
          },
        ],
      },
    ],
  }))

  const TOOLS_NAV_ITEMS: NavigationMenuItem[] = [
    {
      label: 'Scoring: Configuration',
      to: '/content-tools/scoring-algorithm/configuration',
      roles: [AUTH0_ROLE_NAMES.product.id],
    },
    {
      label: 'Scoring: Categories & Attributes',
      to: '/content-tools/scoring-algorithm/categories',
      roles: [AUTH0_ROLE_NAMES.product.id],
    },
    {
      label: 'Claims Testing',
      to: '/content-tools/claims-testing',
    },
    {
      label: 'Predicate Search',
      to: '/content-tools/predicate-search',
    },
    {
      label: 'Document Scoping',
      to: '/content-tools/scoping',
    },
    {
      label: 'Claims Finder',
      to: '/content-tools/claims-finder',
    },
    {
      label: 'Policy Builder',
      to: '/content-tools/policy-builder',
    },
    {
      label: 'Drug Policy Builder',
      to: '/content-tools/drug-builder',
    },
    {
      label: 'Policy Studio',
      to: reviewPolicyStudioRoutes.home(),
    },
    {
      label: 'Policy Studio (Debug)',
      to: debugPolicyStudioRoutes.home(),
      roles: [
        AUTH0_ROLE_NAMES.product.id,
        AUTH0_ROLE_NAMES.engineeringWorkflows.id,
        AUTH0_ROLE_NAMES.engineeringCoreClaims.id,
      ],
    },
    {
      label: 'Import client review status',
      to: '/imports/client-review-status',
      roles: [
        AUTH0_ROLE_NAMES.product.id,
        AUTH0_ROLE_NAMES.clientSuccess.id,
        AUTH0_ROLE_NAMES.clientExperience.id,
        AUTH0_ROLE_NAMES.engineeringWorkflows.id,
      ],
    },
    {
      label: 'Import MCD provider types',
      to: '/imports/mcd-provider-types',
      roles: [
        AUTH0_ROLE_NAMES.product.id,
        AUTH0_ROLE_NAMES.clientSuccess.id,
        AUTH0_ROLE_NAMES.clientExperience.id,
        AUTH0_ROLE_NAMES.engineeringWorkflows.id,
      ],
    },
    {
      label: 'Source Consolidation',
      to: '/content-tools/source-consolidation',
    },
  ]

  const userRoles = computed(
    () => user.value?.['https://auth.rialtic.io/roles'] ?? [],
  )
  const teamItems = computed<NavigationMenuItem[]>((): NavigationMenuItem[] => {
    const navItems = Object.entries(teamNavItems.value)
      .filter(
        ([teamRoleId]) =>
          import.meta.dev || userRoles.value.includes(teamRoleId),
      )
      .flatMap(([, items]) => items)

    if (navItems.length === 0) {
      return []
    }
    return navItems
  })

  const toolsItems = computed<NavigationMenuItem[]>(() => {
    if (import.meta.dev) return TOOLS_NAV_ITEMS

    return TOOLS_NAV_ITEMS.filter(({ roles: itemRoles }) => {
      if (!itemRoles?.length) {
        return true
      }
      return (
        userRoles.value.filter((role) => itemRoles.includes(role)).length > 0
      )
    })
  })

  const isEngineering = computed(
    () =>
      userRoles.value.filter((role) =>
        role.startsWith('rialtic::app_rbac-Engineering'),
      ).length > 0,
  )

  const { data: testItems } = useAsyncData(
    'navigation-menu/tests/all.json',
    async () => {
      const result = await queryCollection('tests')
        .select('id', 'success', 'meta')
        .all()
      return result.map((item) => ({
        id: item.id,
        success: item.success,
        label: titleCase(splitByCase(item.meta?.path as string)),
        trailingIcon: item.success ? 'i-ph-check-circle' : 'i-ph-x-circle',
        to: `/tests${item.meta?.path}`,
      }))
    },
  )

  const engineeringItems = computed<NavigationMenuItem | null>(() => {
    const isProduct = userRoles.value.includes(AUTH0_ROLE_NAMES.product.id)

    if (isProduct || isEngineering.value)
      return {
        label: 'Engineering',
        icon: 'i-ph-code',
        children: [
          {
            label: 'Architecture',
            icon: 'i-ph-graph',
            to: '/architecture',
          },
          {
            label: 'APIs',
            icon: 'i-ph-atom',
            children: [
              {
                label: 'PaAPI',
                to: '/apis/paapi',
              },
            ],
          },
          {
            label: 'Tests',
            icon: 'i-ph-test-tube',
            children: testItems.value,
          },
        ],
      }

    return null
  })

  return {
    defaultItems,
    teamItems,
    toolsItems,
    engineeringItems,
  }
}
